import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import { useNotificationStore } from '@store/notificationStore';
import { useAuthStore, type User } from '@store/authStore';
import type {
  CASelfDeclarationSubmissionRequest,
  InviteVendorRequest,
  NR301SubmissionRequest,
  ValidateTokenResponse,
  VendorLinkListResponse,
  VendorLinkResponse,
  VendorPortalAccessResponse,
  VendorProfileResponse,
  VendorRegisterRequest,
  VendorRegisterResponse,
  UpdateVendorProfileRequest,
  LinkedCustomerListResponse,
  VendorPaymentAccountResponse,
  VendorInvoiceListResponse,
  VendorPaymentStatusResponse,
  VendorTaxPortalResponse,
  UpdateVendorTaxProfileRequest,
  SubmitVendorW9Request,
} from '@schemas/api';

// ============================================================================
// Customer-side: Vendor Links
// ============================================================================

export const useVendorLinks = (skip = 0, limit = 50) => {
  return useQuery({
    queryKey: ['vendor-links', skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorLinkListResponse>(
        '/vendor-links',
        { params: { skip, limit } },
      );
      return data;
    },
  });
};

export const useInviteVendor = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: InviteVendorRequest) => {
      const { data } = await apiClient.post<VendorLinkResponse>(
        '/vendor-links/invite',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-links'] });
      addNotification({ message: 'Vendor invitation sent', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to send invitation',
        type: 'error',
      });
    },
  });
};

export const useResendInvitation = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (linkId: string) => {
      const { data } = await apiClient.post<VendorLinkResponse>(
        `/vendor-links/resend/${linkId}`,
      );
      return data;
    },
    onSuccess: () => {
      addNotification({ message: 'Invitation resent', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to resend invitation',
        type: 'error',
      });
    },
  });
};

// ============================================================================
// Public: Registration
// ============================================================================

export const useValidateVendorToken = (token: string) => {
  return useQuery({
    queryKey: ['vendor-token', token],
    queryFn: async () => {
      const { data } = await apiClient.get<ValidateTokenResponse>(
        '/public/register/vendor/validate-token',
        { params: { token } },
      );
      return data;
    },
    enabled: !!token,
    retry: 0,
  });
};

export const useRegisterVendor = () => {
  return useMutation({
    mutationFn: async (request: VendorRegisterRequest) => {
      const { data } = await apiClient.post<VendorRegisterResponse>(
        '/public/register/vendor',
        request,
      );
      return data;
    },
  });
};

// ============================================================================
// Vendor-side: Profile
// ============================================================================

export const useVendorProfile = () => {
  return useQuery({
    queryKey: ['vendor-profile'],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorProfileResponse>(
        '/vendor-portal/profile',
      );
      return data;
    },
  });
};

export const useUpdateVendorProfile = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: UpdateVendorProfileRequest) => {
      const { data } = await apiClient.put<VendorProfileResponse>(
        '/vendor-portal/profile',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
      addNotification({ message: 'Profile updated successfully', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to update profile',
        type: 'error',
      });
    },
  });
};

export const useVendorTaxProfile = () => {
  return useQuery({
    queryKey: ['vendor-tax-profile'],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorTaxPortalResponse>(
        '/vendor-portal/tax-profile',
      );
      return data;
    },
  });
};

export const useUpdateVendorTaxProfile = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: UpdateVendorTaxProfileRequest) => {
      const { data } = await apiClient.put<VendorTaxPortalResponse>(
        '/vendor-portal/tax-profile',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-tax-profile'] });
      addNotification({ message: 'W-9 tax profile updated', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to update W-9 tax profile',
        type: 'error',
      });
    },
  });
};

export const useSubmitVendorW9 = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: SubmitVendorW9Request) => {
      const { data } = await apiClient.post<VendorTaxPortalResponse>(
        '/vendor-portal/tax-profile/submit-w9',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-tax-profile'] });
      addNotification({ message: 'W-9 certified and submitted', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to submit W-9',
        type: 'error',
      });
    },
  });
};

export const useSubmitCASelfDeclaration = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: CASelfDeclarationSubmissionRequest) => {
      const { data } = await apiClient.post<VendorTaxPortalResponse>(
        '/vendor-portal/tax-profile/submit-ca-self-declaration',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-tax-profile'] });
      addNotification({
        message: 'Canadian self-declaration submitted',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail ||
          'Failed to submit Canadian self-declaration',
        type: 'error',
      });
    },
  });
};

export const useSubmitNR301 = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: NR301SubmissionRequest) => {
      const { data } = await apiClient.post<VendorTaxPortalResponse>(
        '/vendor-portal/tax-profile/submit-nr301',
        request,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-tax-profile'] });
      addNotification({ message: 'NR301 submitted', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to submit NR301',
        type: 'error',
      });
    },
  });
};

// ============================================================================
// Vendor-side: Customers & Invoices
// ============================================================================

export const useVendorPortalAccess = () => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['vendor-portal-access', tenantScope],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorPortalAccessResponse>(
        '/vendor-portal/access',
      );
      return data;
    },
    enabled: Boolean(currentUser),
    retry: 0,
    staleTime: 30 * 1000,
  });
};

export const useLinkedCustomers = () => {
  return useQuery({
    queryKey: ['vendor-customers'],
    queryFn: async () => {
      const { data } = await apiClient.get<LinkedCustomerListResponse>(
        '/vendor-portal/customers',
      );
      return data;
    },
  });
};

export const useVendorInvoices = (
  skip = 0,
  limit = 50,
  customerId?: string,
  status?: string,
) => {
  return useQuery({
    queryKey: ['vendor-invoices', skip, limit, customerId, status],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorInvoiceListResponse>(
        '/vendor-portal/invoices',
        {
          params: {
            skip,
            limit,
            ...(customerId && { customer_id: customerId }),
            ...(status && { status }),
          },
        },
      );
      return data;
    },
  });
};

export const useAcceptVendorInvitation = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  const { setCurrentUser } = useAuthStore();

  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await apiClient.post<VendorRegisterResponse>(
        '/vendor-portal/accept-invitation',
        { token },
      );
      const { data: currentUser } = await apiClient.get<User>('/auth/me');
      return { invitation: data, currentUser };
    },
    onSuccess: ({ invitation, currentUser }) => {
      setCurrentUser(currentUser);
      queryClient.invalidateQueries({ queryKey: ['auth', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-portal-access'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-customers'] });
      addNotification({ message: invitation.message, type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to accept invitation',
        type: 'error',
      });
    },
  });
};

export const useVendorPaymentLoginLink = () => {
  return useMutation({
    mutationFn: async (customerTenantId: string) => {
      const { data } = await apiClient.post<{ url: string }>(
        '/vendor-portal/payment-login-link',
        { customer_tenant_id: customerTenantId },
      );
      return data;
    },
  });
};

export const useVendorPaymentAccount = () => {
  return useQuery({
    queryKey: ['vendor-payment-account'],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorPaymentAccountResponse>(
        '/vendor-portal/payment-account',
      );
      return data;
    },
  });
};

export const useStartVendorPaymentAccount = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload?: { return_path?: string; email?: string; country?: string; currency?: string }) => {
      const { data } = await apiClient.post<VendorPaymentAccountResponse>(
        '/vendor-portal/payment-account/start',
        payload ?? {},
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-account'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-status'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to start vendor Stripe setup',
        type: 'error',
      });
    },
  });
};

export const useRefreshVendorPaymentAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<VendorPaymentAccountResponse>(
        '/vendor-portal/payment-account/refresh',
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-account'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payment-status'] });
    },
  });
};

export const useVendorPaymentAccountLoginLink = () => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ url: string }>(
        '/vendor-portal/payment-account/login-link',
      );
      return data;
    },
  });
};

export const useVendorPaymentStatus = () => {
  return useQuery({
    queryKey: ['vendor-payment-status'],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorPaymentStatusResponse[]>(
        '/vendor-portal/payment-status',
      );
      return data;
    },
  });
};
